import { FC, useEffect } from "react";
import { Theme } from "../../../util/style";

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

:root {
  --bg: #0d1117; --surface: #161b22; --surface2: #1c2330; --border: #21262d;
  --accent: #2563EB; --accent2: #0095ff; --accent3: #ff6b6b; --accent4: #ffd166;
  --text: #e6edf3; --text-muted: #7d8590; --text-dim: #484f58; --sw: 240px;
  font-family: 'DM Sans', sans-serif;
}

html.theme-light {
  --bg: #f8fafc; --surface: #ffffff; --surface2: #f1f5f9; --border: #e2e8f0;
  --text: #0f172a; --text-muted: #64748b; --text-dim: #94a3b8;
}

html.theme-dark {
  --bg: #0d1117; --surface: #161b22; --surface2: #1c2330; --border: #21262d;
  --text: #e6edf3; --text-muted: #7d8590; --text-dim: #484f58;
}

html, body, #root { height: 100%; background: var(--bg); color: var(--text); }

::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── LAYOUT ── */
.layout { display: flex; height: 100vh; overflow: hidden; }
.sidebar {
  width: var(--sw); flex-shrink: 0; height: 100vh;
  background: var(--surface); border-right: 1px solid var(--border);
  display: flex; flex-direction: column;
}
.sidebar-logo { padding: 24px 20px 20px; border-bottom: 1px solid var(--border); }
.sidebar-brand { font-family: 'DM Serif Display', serif; font-size: 20px; color: var(--accent); }
.sidebar-sub { font-size: 11px; color: var(--text-muted); margin-top: 2px; letter-spacing: 1px; text-transform: uppercase; }
.sidebar-nav { flex: 1; padding: 16px 0; overflow-y: auto; }
.nav-group-label { font-size: 10px; color: var(--text-dim); letter-spacing: 1.5px; text-transform: uppercase; padding: 8px 20px 4px; }
.nav-item {
  display: flex; align-items: center; gap: 10px; padding: 9px 20px;
  transition: all .2s; border-left: 3px solid transparent;
  color: var(--text-muted); font-size: 13.5px; font-weight: 500;
  text-decoration: none; cursor: pointer; background: none; border-right: none; border-top: none; border-bottom: none; width: 100%; text-align: left;
}
.nav-item:hover { color: var(--text); background: rgba(255,255,255,.04); }
.nav-item.active { color: var(--accent); border-left-color: var(--accent); background: rgba(0,212,170,.07); }
.nav-item .nav-icon { font-size: 16px; width: 18px; text-align: center; }
.nav-badge { margin-left: auto; background: var(--accent3); color: #fff; font-size: 10px; padding: 1px 6px; border-radius: 10px; font-weight: 600; }
.sidebar-footer { padding: 16px 20px; border-top: 1px solid var(--border); }
.sidebar-user { display: flex; align-items: center; gap: 10px; }
.user-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), var(--accent2));
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #fff; flex-shrink: 0;
}
.user-name { font-size: 13px; font-weight: 600; }
.user-role { font-size: 11px; color: var(--text-muted); }

/* ── MAIN ── */
.main { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
.topbar {
  flex-shrink: 0; background: rgba(13,17,23,.9); backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border); padding: 0 32px; height: 60px;
  display: flex; align-items: center; justify-content: space-between;
}
.page-title { font-family: 'DM Serif Display', serif; font-size: 20px; }
.breadcrumb { font-size: 12px; color: var(--text-muted); }
.topbar-right { display: flex; align-items: center; gap: 10px; }

/* ── CONTENT ── */
.content { flex: 1; overflow-y: auto; padding: 28px 32px 40px; }

/* ── BUTTONS ── */
.btn {
  background: var(--surface); border: 1px solid var(--border); color: var(--text-muted);
  padding: 7px 14px; border-radius: 8px; cursor: pointer; font-size: 13px;
  font-family: 'DM Sans', sans-serif; display: inline-flex; align-items: center;
  gap: 6px; transition: all .2s; white-space: nowrap;
}
.btn:hover { border-color: var(--accent); color: var(--accent); }
.btn.btn-primary { background: var(--accent); border-color: var(--accent); color: #0d1117; font-weight: 600; }
.btn.btn-primary:hover { background: #00f0c0; }
.btn.btn-sm { padding: 4px 9px; font-size: 11px; }
.btn.btn-danger:hover { border-color: var(--accent3); color: var(--accent3); }

/* ── FILTER BAR ── */
.filter-bar {
  background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
  padding: 12px 18px; margin-bottom: 18px;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.filter-input {
  flex: 1; min-width: 200px; background: var(--surface2); border: 1px solid var(--border);
  color: var(--text); padding: 8px 14px; border-radius: 8px; font-size: 13px;
  font-family: 'DM Sans', sans-serif; outline: none; transition: border-color .2s;
}
.filter-input:focus { border-color: var(--accent); }
.filter-input::placeholder { color: var(--text-dim); }
.filter-select {
  background: var(--surface2); border: 1px solid var(--border); color: var(--text-muted);
  padding: 8px 12px; border-radius: 8px; font-size: 13px;
  font-family: 'DM Sans', sans-serif; outline: none; cursor: pointer;
}

/* ── TABLE CARD ── */
.table-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-header {
  padding: 14px 20px; border-bottom: 1px solid var(--border);
  display: flex; align-items: center; justify-content: space-between;
}
.table-title { font-size: 14px; font-weight: 600; }
.table-meta { font-size: 12px; color: var(--text-muted); }
.table-scroll { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
thead th {
  padding: 10px 14px; text-align: left; font-size: 11px; 
  text-transform: uppercase; letter-spacing: .8px; border-bottom: 1px solid var(--border);
  font-weight: 500; white-space: nowrap;
}
tbody td { padding: 11px 14px; border-bottom: 1px solid rgba(33,38,45,.5); vertical-align: middle; }
tbody tr:last-child td { border-bottom: none; }
tbody tr:hover td { background: rgba(255,255,255,.02); }

/* ── BADGES ── */
.badge { padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.badge-green { background: rgba(0,212,170,.12); color: var(--accent); }
.badge-blue { background: rgba(0,149,255,.12); color: var(--accent2); }
.badge-yellow { background: rgba(255,209,102,.12); color: var(--accent4); }
.badge-red { background: rgba(255,107,107,.12); color: var(--accent3); }

/* ── EMP ROW ── */
.emp-row { display: flex; align-items: center; gap: 10px; }
.emp-avatar {
  width: 34px; height: 34px; border-radius: 50%;
  background: linear-gradient(135deg, #1c2330, #21262d);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: var(--accent);
  flex-shrink: 0; border: 1px solid var(--border);
}
.emp-name { font-weight: 500; font-size: 13px; }
.emp-sub { font-size: 11px; color: var(--text-muted); }

/* ── JSON TAGS ── */
.json-tags { display: flex; flex-wrap: wrap; gap: 4px; max-width: 180px; }
.json-tag {
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 4px; padding: 2px 6px; font-size: 10px;
  color: var(--text-muted); font-family: monospace;
}
.json-tag.key { color: var(--accent2); }

/* ── PAGINATION ── */
.pagination {
  padding: 14px 20px; border-top: 1px solid var(--border);
  display: flex; align-items: center; justify-content: space-between;
}
.pagination-info { font-size: 13px; color: var(--text-muted); }
.pagination-btns { display: flex; gap: 5px; }
.page-btn {
  background: var(--surface2); border: 1px solid var(--border); color: var(--text-muted);
  padding: 5px 11px; border-radius: 6px; cursor: pointer; font-size: 12px;
  font-family: 'DM Sans', sans-serif; transition: all .2s;
}
.page-btn:hover, .page-btn.active {
  border-color: var(--accent); color: var(--accent); background: rgba(0,212,170,.07);
}
.page-btn:disabled { opacity: .35; cursor: not-allowed; }
`;

export const GlobalStyles: FC<{ theme: Theme }> = ({ theme }) => {
  useEffect(() => {
    const id = "hr-karyawan-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(theme === 'dark' ? 'theme-dark' : 'theme-light');
  }, [theme]);

  return null;
};